import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CPS_UI_ADMIN_CONFIG_TYPE, CpsMaintenancePageState } from '@cs-cms/types';
import { useCurrentUserContext, useUiAdminConfigsContext } from '@cs-cms/state-management';
import { AccessDeniedPage, CpsMessageBar, RoutePath } from '@cs-cms/ui';
import CpsLoader from '@cs-cms/ui/common/CpsLoader';
import HomePage from '@pages/home/ui/HomePage';

const SettingsPage = lazy(() => import('@pages/settings/ui/GearSettingsPage'));
const ItAdminSettingsPage = lazy(() => import('@cs-cms/ui/pages/ItAdminSettingsPage'));
const MaintenancePage = lazy(() => import('@cs-cms/ui/pages/MaintenancePage'));
const LogoutPage = lazy(() => import('@cs-cms/ui/pages/LogoutPage'));
const NoMatchPage = lazy(() => import('@cs-cms/ui/pages/NoMatchPage'));

const Root: React.FC = () => {
  const { currentUser, isLoading } = useCurrentUserContext();
  const { uiAdminConfigState, uiAdminConfigWritePermissions, setConfigWritePermissionsKeys } =
    useUiAdminConfigsContext();

  React.useEffect(() => {
    if (setConfigWritePermissionsKeys) {
      setConfigWritePermissionsKeys([CPS_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE]);
    }
  }, [setConfigWritePermissionsKeys]);

  if (isLoading || !uiAdminConfigWritePermissions || !uiAdminConfigState.maintenancePage) {
    return <CpsLoader />;
  }

  if (!currentUser || currentUser.indexes?.length === 0) {
    return (
      <Suspense fallback={<CpsLoader />}>
        <Routes>
          <Route path="*" element={<AccessDeniedPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (
    !uiAdminConfigWritePermissions.maintenancePage &&
    (uiAdminConfigState.maintenancePage as CpsMaintenancePageState)?.enabled
  ) {
    return (
      <Suspense fallback={<CpsLoader />}>
        <Routes>
          <Route path="*" element={<MaintenancePage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <>
      <CpsMessageBar />
      <Suspense fallback={<CpsLoader />}>
        <Routes>
          <Route path={RoutePath.HOME} element={<HomePage />} />
          <Route path={RoutePath.SETTINGS}>
            <Route index element={<Navigate to={RoutePath.SETTINGS_VIEWS} replace />} />
            <Route path={RoutePath.SETTINGS_VIEWS} element={<SettingsPage />} />
            <Route path={RoutePath.SETTINGS_HELP_CENTER} element={<SettingsPage />} />
            <Route path={RoutePath.SETTINGS_ADMIN_CENTER} element={<SettingsPage />} />
            <Route path={RoutePath.SETTINGS_IT_ADMIN_CENTER} element={<ItAdminSettingsPage />} />
          </Route>
          <Route path={RoutePath.LOGOUT} element={<LogoutPage />} />
          <Route path={RoutePath.MAINTENANCE} element={<MaintenancePage />} />
          <Route path="*" element={<NoMatchPage />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default Root;
